import { ArcusMark, type ArcusMarkProps } from '@/components/icons/ArcusMark';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

/**
 * Estado de carregamento da aplicação, com o símbolo em varredura.
 *
 * ## Por que o texto é sempre visível
 *
 * O reset global de `prefers-reduced-motion` (`src/index.css`) trava
 * `animation-iteration-count: 1`. Para quem pediu menos movimento — e para
 * quem está numa aba em segundo plano, onde o navegador suspende animação — a
 * marca simplesmente **para**. Um loader cujo único sinal de "estou vivo" é o
 * giro vira, nesses casos, um logo parado no meio da tela.
 *
 * Por isso o rótulo não é `sr-only`: ele é o afordance que sobrevive à ausência
 * de movimento. O giro é reforço, não a mensagem.
 *
 * ## Quando não usar
 *
 * Para conteúdo cuja forma já é conhecida (lista, card, detalhe), o certo é
 * skeleton, não spinner — docs/03-componentes-e-ui.md §2. Este componente é
 * para espera **sem layout a antecipar**: boot da aplicação e troca de rota.
 */

export interface ArcusLoadingProps {
  /** Mensagem anunciada e exibida. Diga o que está carregando, se souber. */
  label?: string;
  /** Sobre qual superfície o loader vai. `on-dark` é o padrão do splash. */
  tone?: 'on-dark' | 'on-light';
  /**
   * Ritmo da varredura. `restless` por padrão: espera pede leitura de atividade,
   * e a propagação ordenada de `wave` lê como calma.
   */
  sweep?: ArcusMarkProps['sweep'];
  className?: string;
}

export function ArcusLoading({
  label = 'Carregando…',
  tone = 'on-light',
  sweep = 'restless',
  className,
}: ArcusLoadingProps) {
  return (
    <div
      // `polite` e não `assertive`: carregar não interrompe o que o leitor de
      // tela está dizendo. O `role="status"` já implica `aria-live`, mas
      // declarar torna o comportamento explícito para quem lê o componente.
      role="status"
      aria-live="polite"
      className={cn('flex flex-col items-center justify-center gap-4', className)}
    >
      <ArcusMark motion="sweep" tone={tone} sweep={sweep} className="size-16" />
      {/*
       * Sobre `shell` o cinza de `tone="muted"` reprova em contraste, então a
       * cor vem por className — `shell-muted-foreground` não é um tom de
       * `<Text>` porque só existe sobre a superfície escura.
       */}
      <Text
        variant="subtitle"
        tone={tone === 'on-dark' ? 'default' : 'muted'}
        className={cn(tone === 'on-dark' && 'text-shell-muted-foreground')}
      >
        {label}
      </Text>
    </div>
  );
}
